import { Icon } from './Icon.js';
import {
  isInputField,
  stringifyValue,
  type FormValues,
  type Page,
} from './lib/steps.js';

export interface ReviewProps {
  pages: Page[];
  values: FormValues;
  /** Cascading visibility map for the whole form (see visibleFields). */
  visible: Record<string, boolean>;
  /** Jump back to the page at this index for editing. */
  onEdit: (pageIndex: number) => void;
  turnstileEnabled: boolean;
  turnstileChecked: boolean;
  onToggleTurnstile: () => void;
}

export function Review({
  pages,
  values,
  visible,
  onEdit,
  turnstileEnabled,
  turnstileChecked,
  onToggleTurnstile,
}: ReviewProps) {
  return (
    <div>
      <div className="rdr-review-list">
        {pages.map((p, i) => {
          const rows = p.fields
            .filter(isInputField)
            .filter((f) => visible[f.id])
            .map((f) => ({
              label: f.label,
              value:
                f.type === 'consent'
                  ? values[f.id] === true
                    ? 'Agreed'
                    : 'Not agreed'
                  : stringifyValue(values[f.id]),
            }));
          // A step with nothing to answer (every question hidden by its rules,
          // or the step skipped) isn't listed, as in the progress bar.
          if (!rows.length) return null;
          return (
            <div key={i} className="rdr-review-section">
              <div className="rdr-review-head">
                <span className="rdr-review-title">
                  <Icon
                    name="check"
                    size={15}
                    style={{ color: 'var(--st-approved)' }}
                  />
                  {p.title}
                </span>
                <button
                  type="button"
                  className="rdr-edit-link"
                  onClick={() => onEdit(i)}
                  aria-label={`Change ${p.title}`}
                >
                  Change
                </button>
              </div>
              <dl className="rdr-review-rows">
                {rows.map((r, j) => (
                  <div key={j} className="rdr-review-row">
                    <dt>{r.label}</dt>
                    <dd>
                      {r.value || (
                        <span className="rdr-review-empty">Not answered</span>
                      )}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
          );
        })}
      </div>

      {turnstileEnabled && (
        // Placeholder: the real Cloudflare Turnstile widget (and its token)
        // replaces this checkbox in a later build step.
        <button
          type="button"
          className="rdr-turnstile"
          onClick={onToggleTurnstile}
          aria-pressed={turnstileChecked}
        >
          <span
            className={'rdr-turnstile-box' + (turnstileChecked ? ' on' : '')}
          >
            {turnstileChecked && <Icon name="check" size={14} />}
          </span>
          <span className="rdr-turnstile-label">
            I&rsquo;m not a robot
          </span>
        </button>
      )}
    </div>
  );
}
